<script setup lang="ts">
/**
 * The mobile navigation surface — MAR-123.
 *
 * Below `sm` both rails hide and the light region shows exactly one column, so this bar is the only
 * way to move between panes and destinations. Above `sm` it is not rendered at all (`sm:hidden`) and
 * the rails are back.
 *
 * **Two kinds of tab, deliberately mixed.** Chat and Documentos switch the visible PANE without
 * navigating; Clientes and Leyes navigate. They look identical because to the user they are the same
 * gesture — "show me that" — and the distinction between a pane and a route is an implementation
 * detail of the desktop shell. `useMobilePane` reconciles them: a navigation clears the pane
 * override (see `PanelLayout`), so a route tab always lands on the page it named.
 *
 * Five slots is the ceiling for a thumb-reachable bar, and the rail has nine destinations plus the
 * user chip. The four that did not fit live behind "Más", which is a sheet rather than a menu so the
 * targets stay finger-sized.
 */
import {
  Building2, FilePen, FolderOpen, Landmark, MessageSquare,
  MoreHorizontal, Shield, TableProperties, Clock, Settings, X, LogOut,
} from 'lucide-vue-next'
import { Dialog, DialogClose, DialogContent, DialogTitle } from '~/components/ui/dialog'

const { t } = useI18n()
const route = useRoute()
const { profile, initials, displayName, organizationName } = useUserProfile()
const { signOut } = useAuth()
const { pane, show } = useMobilePane()

const moreOpen = ref(false)
const isSuperAdmin = computed(() => !!profile.value?.is_superadmin)

/** The two overflow destinations that are always present, plus the superadmin console. */
const moreItems = computed(() => [
  { icon: TableProperties, to: '/app/sheets', label: t('sheets.title') },
  { icon: FilePen, to: '/app/workflows', label: t('nav.workflows') },
  { icon: Clock, to: '/app/history', label: t('nav.history') },
  ...(isSuperAdmin.value ? [{ icon: Shield, to: '/app/admin', label: t('nav.admin') }] : []),
  { icon: Settings, to: '/app/settings', label: t('nav.settings') },
])

/** A route tab is active when the URL matches AND the content pane is the one on screen — otherwise
 *  tapping Chat would leave Clientes looking selected while chat is what you are reading. */
/** Variadic because Expedientes is one tab over two sibling routes (/app/cases + /app/clients, MAR-130). */
function routeActive(...prefixes: string[]) {
  return prefixes.some(p => route.path.startsWith(p)) && pane.value === 'content'
}

/** `signOut` clears the session and navigates to `/login` itself; the flag only stops a double tap. */
const signingOut = ref(false)
async function handleSignOut() {
  signingOut.value = true
  await signOut()
}

function goTo(to: string) {
  moreOpen.value = false
  return navigateTo(to)
}
</script>

<template>
  <!-- `pb-[env(safe-area-inset-bottom)]`: the bar is the last thing above the home indicator on a
       notched iPhone, and without the inset the tap targets sit under it. -->
  <nav
      class="sm:hidden shrink-0 flex items-stretch bg-sidebar text-sidebar-foreground border-t border-sidebar-border pb-[env(safe-area-inset-bottom)]"
  >
    <button
        type="button"
        class="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
        :class="pane === 'chat' ? 'text-sidebar-foreground' : 'text-sidebar-icon'"
        @click="show('chat')"
    >
      <MessageSquare class="h-5 w-5"/>
      <span class="text-[10px] leading-none">{{ t('nav.chat') }}</span>
    </button>

    <button
        type="button"
        class="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
        :class="pane === 'docs' ? 'text-sidebar-foreground' : 'text-sidebar-icon'"
        @click="show('docs')"
    >
      <FolderOpen class="h-5 w-5"/>
      <span class="text-[10px] leading-none">{{ t('nav.documents') }}</span>
    </button>

    <button
        type="button"
        class="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
        :class="routeActive('/app/clients', '/app/cases') ? 'text-sidebar-foreground' : 'text-sidebar-icon'"
        @click="goTo('/app/clients')"
    >
      <Building2 class="h-5 w-5"/>
      <span class="text-[10px] leading-none">{{ t('nav.clients') }}</span>
    </button>

    <button
        type="button"
        class="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
        :class="routeActive('/app/laws') ? 'text-sidebar-foreground' : 'text-sidebar-icon'"
        @click="goTo('/app/laws')"
    >
      <Landmark class="h-5 w-5"/>
      <span class="text-[10px] leading-none">{{ t('nav.laws') }}</span>
    </button>

    <button
        type="button"
        class="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
        :class="moreOpen ? 'text-sidebar-foreground' : 'text-sidebar-icon'"
        :aria-expanded="moreOpen"
        @click="moreOpen = true"
    >
      <MoreHorizontal class="h-5 w-5"/>
      <span class="text-[10px] leading-none">{{ t('nav.more') }}</span>
    </button>
  </nav>

  <!-- The overflow sheet: a `ui/dialog` pinned to the bottom edge (MAR-948), so it is announced,
       traps focus, closes on Escape or a tap on the dimmed page, and hands focus back to "Más".
       Portalled like every dialog, so it covers the shell instead of being trapped inside a ~56px
       band's stacking context. -->
  <Dialog v-model:open="moreOpen">
    <DialogContent
        class="top-auto bottom-0 left-0 max-w-none translate-x-0 translate-y-0 gap-0 rounded-none rounded-t-3xl sm:rounded-none sm:rounded-t-3xl border-0 bg-sidebar p-0 text-sidebar-foreground pb-[env(safe-area-inset-bottom)] max-h-dvh overflow-y-auto data-[state=open]:animate-none data-[state=closed]:animate-none"
        :show-close-button="false"
        :aria-describedby="undefined"
    >
        <div class="flex items-center justify-between px-5 pt-4 pb-2">
          <DialogTitle class="text-[13px] font-medium leading-normal tracking-normal">{{ t('nav.more') }}</DialogTitle>
          <DialogClose
              class="flex items-center justify-center h-8 w-8 rounded-lg text-sidebar-icon hover:bg-sidebar-hover transition-colors"
              :aria-label="t('common.close')"
          >
            <X class="h-4 w-4"/>
          </DialogClose>
        </div>
        <!-- The account, first (MAR-1350): the user chip, organization and sign-out live in `IconRail`,
             which is hidden below `sm`, so on a phone this is the only place they exist. -->
        <div
            data-mobile-account
            class="flex items-center gap-3 mx-5 mb-2 py-3 border-b border-sidebar-border"
        >
          <div
              class="h-9 w-9 rounded-full bg-sidebar-hover text-sidebar-foreground border border-sidebar-border flex items-center justify-center text-[11px] font-semibold shrink-0"
              aria-hidden="true"
          >
            {{ initials }}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-[14px] font-medium truncate">{{ displayName }}</p>
            <p v-if="organizationName && !profile?.org_override_id" class="text-[12px] text-sidebar-muted truncate">{{ organizationName }}</p>
          </div>
          <button
              type="button"
              class="shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg text-[13px] text-sidebar-foreground hover:bg-sidebar-hover transition-colors disabled:opacity-50"
              :disabled="signingOut"
              @click="handleSignOut"
          >
            <LogOut class="h-4 w-4 text-sidebar-icon"/>
            {{ t('auth.signOut') }}
          </button>
        </div>
        <div class="pb-3">
          <button
              v-for="item in moreItems"
              :key="item.to"
              type="button"
              class="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors"
              :class="route.path.startsWith(item.to)
                ? 'bg-sidebar-active text-sidebar-active-foreground font-medium'
                : 'hover:bg-sidebar-hover'"
              @click="goTo(item.to)"
          >
            <component
                :is="item.icon"
                class="h-4 w-4 shrink-0"
                :class="route.path.startsWith(item.to) ? 'text-sidebar-active-foreground' : 'text-sidebar-icon'"
            />
            <span class="text-[14px]">{{ item.label }}</span>
          </button>
        </div>
    </DialogContent>
  </Dialog>
</template>
